import React, { useState, useEffect } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Input, FormGroup } from 'reactstrap';
import api from '../../api';

const PlayerShopLotModal = ({ isOpen, toggle }) => {
  const [duration, setDuration] = useState(1); // Default to 1 month
  const [totalCost, setTotalCost] = useState(600); // 20 coins per day * 30 days
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [userCoins, setUserCoins] = useState(0);
  const [availableLots, setAvailableLots] = useState([]);
  const [selectedLot, setSelectedLot] = useState(null);
  
  const DAILY_RATE = 20; // 20 coins per day
  const DAYS_PER_MONTH = 30;

  const [userOwnedLot, setUserOwnedLot] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchUserCoins();
      fetchAvailableLots();
      checkUserOwnedLot();
    }
  }, [isOpen]);

  useEffect(() => {
    // Calculate total cost whenever duration changes
    setTotalCost(duration * DAYS_PER_MONTH * DAILY_RATE);
  }, [duration]);

  const fetchUserCoins = async () => {
    try {
      // Use the correct profile endpoint
      const response = await api.get('/v1/profile', { withCredentials: true });
      setUserCoins(response.data.coins || 0);
    } catch (err) {
      console.error('Error fetching user coins:', err);
      setError('Could not fetch your coin balance. Please try again.');
    }
  };

  const fetchAvailableLots = async () => {
    try {
      setLoading(true);
      const response = await api.get('/v1/player-shops/available-lots', { withCredentials: true });
      // The response is the array directly, not nested in a 'lots' property
      setAvailableLots(response.data || []);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching available lots:', err);
      setError('Could not fetch available lots. Please try again.');
      setLoading(false);
    }
  };

  const checkUserOwnedLot = async () => {
    try {
      // Check if user already owns a lot
      const response = await api.get('/v1/player-shops/my-lots', { withCredentials: true });
      setUserOwnedLot(response.data && response.data.length > 0);
      if (response.data && response.data.length > 0) {
        setError('You already own a shop lot. You can only own one lot at a time.');
      }
    } catch (err) {
      console.error('Error checking user owned lots:', err);
    }
  };

  const handleDurationChange = (e) => {
    const value = parseInt(e.target.value);
    if (value >= 1 && value <= 6) {
      setDuration(value);
    }
  };

  const handleLotSelection = (lotId) => {
    setSelectedLot(lotId);
  };

  const handlePurchase = async () => {
    if (!selectedLot) {
      setError('Please select a lot first.');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      // This endpoint will need to be implemented in the backend
      await api.post('/v1/player-shops/purchase-lot', {
        lot_id: selectedLot,
        duration_months: duration
      }, { withCredentials: true });
      
      setSuccess(true);
      setLoading(false);
      
      // Refresh user's coin balance
      fetchUserCoins();
      
      // After 3 seconds, close the modal
      setTimeout(() => {
        toggle();
        setSuccess(false);
      }, 3000);
      
    } catch (err) {
      console.error('Error purchasing lot:', err);
      setError(err.response?.data?.detail || 'Failed to purchase lot. Please try again.');
      setLoading(false);
    }
  };

  // Format number with commas
  const formatNumber = (num) => {
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered className="player-shop-lot-modal">
      <ModalHeader toggle={toggle}>
        Purchase a Shop Lot
      </ModalHeader>
      <ModalBody>
        {error && (
          <div className="alert alert-danger">{error}</div>
        )}
        
        {success ? (
          <div className="text-center">
            <div className="alert alert-success">
              <h4>Congratulations!</h4>
              <p>You have successfully purchased a shop lot for {duration} month{duration > 1 ? 's' : ''}!</p>
              <p>This window will close automatically...</p>
            </div>
          </div>
        ) : (
          <>
            <div className="mb-4">
              <h5>Available Lots</h5>
              {loading ? (
                <p>Loading available lots...</p>
              ) : availableLots.length === 0 ? (
                <p>No lots are currently available. Please check back later.</p>
              ) : (
                <div className="lot-selection">
                  {/* Group lots by location */}
                  {Object.entries(availableLots.reduce((acc, lot) => {
                    if (!acc[lot.location]) acc[lot.location] = [];
                    acc[lot.location].push(lot);
                    return acc;
                  }, {})).map(([location, lots]) => (
                    <div key={location} className="lot-location-group mb-3">
                      <h6>{location}</h6>
                      <div className="d-flex flex-wrap">
                        {lots.map(lot => (
                          <div 
                            key={lot.id}
                            className={`lot-item m-1 p-2 border ${selectedLot === lot.id ? 'selected bg-primary text-white' : 'bg-light'}`}
                            onClick={() => handleLotSelection(lot.id)}
                            style={{ cursor: 'pointer', borderRadius: '4px', width: '100px' }}
                          >
                            <div className="lot-number">Lot #{lot.lot_number}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <div className="mb-4">
              <h5>Rental Duration</h5>
              <p>Select how long you want to rent this lot (1-6 months):</p>
              <FormGroup>
                <Input
                  type="range"
                  min="1"
                  max="6"
                  value={duration}
                  onChange={handleDurationChange}
                  className="custom-range"
                />
                <div className="d-flex justify-content-between">
                  <span>1 Month</span>
                  <span>6 Months</span>
                </div>
              </FormGroup>
              <div className="text-center mt-2">
                <h6>Selected: {duration} Month{duration > 1 ? 's' : ''}</h6>
              </div>
            </div>
            
            <div className="cost-summary">
              <div className="d-flex justify-content-between align-items-center">
                <span>Daily Rate:</span>
                <span><img src="items/coin.png" alt="Coin" style={{ height: '16px', marginRight: '5px' }} /> {formatNumber(DAILY_RATE)} coins</span>
              </div>
              <div className="d-flex justify-content-between align-items-center">
                <span>Duration:</span>
                <span>{duration} month{duration > 1 ? 's' : ''} ({duration * DAYS_PER_MONTH} days)</span>
              </div>
              <div className="d-flex justify-content-between align-items-center font-weight-bold mt-2">
                <span>Total Cost:</span>
                <span><img src="items/coin.png" alt="Coin" style={{ height: '16px', marginRight: '5px' }} /> {formatNumber(totalCost)} coins</span>
              </div>
              <div className="d-flex justify-content-between align-items-center mt-2">
                <span>Your Balance:</span>
                <span><img src="items/coin.png" alt="Coin" style={{ height: '16px', marginRight: '5px' }} /> {formatNumber(userCoins)} coins</span>
              </div>
              {userCoins < totalCost && (
                <div className="alert alert-warning mt-2">
                  You don't have enough coins for this purchase.
                </div>
              )}
            </div>
          </>
        )}
      </ModalBody>
      <ModalFooter>
        {!success && (
          <>
            <Button color="secondary" onClick={toggle}>
              Cancel
            </Button>
            <Button 
              color="primary" 
              onClick={handlePurchase} 
              disabled={loading || userCoins < totalCost || !selectedLot || userOwnedLot}
            >
              {loading ? 'Processing...' : `Purchase (${formatNumber(totalCost)} coins)`}
            </Button>
          </>
        )}
      </ModalFooter>
    </Modal>
  );
};

export default PlayerShopLotModal;
